/**
 * User API Functions and Hooks
 *
 * Provides TanStack Query hooks for user CRUD operations.
 * - Signup: public endpoint, requires a special signup code
 * - List / Update / Delete: authenticated endpoints
 * - Change password: authenticated, acts on the caller's own account
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User, UserRole } from '@course-authoring/shared-types';
import { createJsonHeaders } from './apiUtils';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Query key factory for user-related queries
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  detail: (id: string) => [...userKeys.all, 'detail', id] as const,
};

/**
 * API Error type (matches courseApi pattern)
 */
export interface UserApiError {
  status: number;
  code: string;
  message: string;
  retryable: boolean;
  retryAfterSeconds?: number;
}

function isRetryableError(status: number): boolean {
  return status >= 500 || status === 429;
}

async function handleErrorResponse(response: Response, fallbackMessage: string, retryAfterSeconds?: number): Promise<never> {
  let message = fallbackMessage;
  try {
    const body = await response.json();
    if (typeof body?.message === 'string' && body.message.trim()) {
      message = body.message;
    }
  } catch {
    // use fallback message
  }
  const error: UserApiError = {
    status: response.status,
    code: `HTTP_${response.status}`,
    message,
    retryable: isRetryableError(response.status),
    ...(retryAfterSeconds !== undefined ? { retryAfterSeconds } : {}),
  };
  throw error;
}

// ---------------------------------------------------------------------------
// Signup (public – requires signup code)
// ---------------------------------------------------------------------------

export interface SignupPayload {
  name: string;
  email: string;
  password: string;
  signupCode: string;
}

interface SignupResponse {
  user: User;
}

function parseRetryAfter(header: string | null): number | undefined {
  const value = header?.trim();
  if (!value) return undefined;

  // HTTP dates begin with a weekday; don't let Date.parse reinterpret invalid
  // numeric delays such as "-1" or "1.5" as calendar dates.
  const seconds = /^\d+$/.test(value)
    ? Number(value)
    : /^[A-Za-z]/.test(value)
      ? Math.max(0, Math.ceil((Date.parse(value) - Date.now()) / 1000))
      : NaN;
  return Number.isSafeInteger(seconds) && seconds >= 0 ? seconds : undefined;
}

async function signup(payload: SignupPayload): Promise<SignupResponse | undefined> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      credentials: 'include',
      headers: createJsonHeaders(),
      body: JSON.stringify(payload),
    });
  } catch {
    const error: UserApiError = {
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Account creation could not be confirmed because of a connection problem.',
      retryable: false,
    };
    throw error;
  }

  if (!response.ok) {
    await handleErrorResponse(response, 'Signup failed', parseRetryAfter(response.headers.get('Retry-After')));
  }

  try {
    return await response.json();
  } catch {
    // The 2xx confirms creation and cookie setup. The page can hydrate the
    // session on its normal redirect without needing the response's user data.
    return undefined;
  }
}

export function useSignupMutation(options?: {
  onSuccess?: (data: SignupResponse | undefined) => void;
  onError?: (error: UserApiError) => void;
}) {
  return useMutation<SignupResponse | undefined, UserApiError, SignupPayload>({
    mutationFn: (payload: SignupPayload) => signup(payload),
    // A failed or unconfirmed write must not be replayed by the app-wide default.
    retry: false,
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ---------------------------------------------------------------------------
// Change own password (authenticated)
// ---------------------------------------------------------------------------

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

interface ChangePasswordResponse {
  message: string;
}

/**
 * The confirm-new-password field is deliberately absent: matching a confirmation
 * is a client-side concern, and the server has no way to tell a typo from a
 * deliberate mismatch. The account changed is always the caller's own — the
 * payload carries no id.
 */
async function changePassword(payload: ChangePasswordPayload): Promise<ChangePasswordResponse> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/auth/change-password`, {
      method: 'POST',
      credentials: 'include',
      headers: createJsonHeaders(),
      body: JSON.stringify(payload),
    });
  } catch {
    // fetch only rejects on a transport failure. Normalise it into the same
    // shape as an HTTP error so callers have exactly one error type to handle,
    // and mark it retryable — the caller must not report this as a wrong
    // password.
    const error: UserApiError = {
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Could not reach the server. Please try again.',
      retryable: true,
    };
    throw error;
  }

  if (!response.ok) {
    await handleErrorResponse(response, 'Failed to change password');
  }

  try {
    return await response.json();
  } catch {
    // A 2xx means the password was changed; the endpoint only responds after
    // the write. An unparseable body is not a reason to tell the user it failed.
    return { message: 'Password changed successfully' };
  }
}

export function useChangePasswordMutation(options?: {
  onSuccess?: (data: ChangePasswordResponse) => void;
  onError?: (error: UserApiError) => void;
}) {
  return useMutation({
    mutationFn: (payload: ChangePasswordPayload) => changePassword(payload),
    // Never retry automatically. The app-wide default is 3 retries with
    // exponential backoff (see queryClient.ts), which is wrong here in three
    // separate ways:
    //  - a rejected password is deterministic, so retrying only makes the user
    //    wait ~7s of backoff to be told they typed it wrong;
    //  - each attempt spends one of the endpoint's 10-per-15-minute budget, so
    //    a few typos would lock the user out;
    //  - on a 5xx raised *after* the write landed, the retry would come back
    //    "current password is incorrect" — the password has already changed —
    //    which is a confusing lie.
    // The dialog keeps the user's fields on failure, so retrying is one click.
    retry: false,
    // No query invalidation on purpose: nothing cached describes the password,
    // and the session stays valid, so there is nothing to refetch.
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as UserApiError);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// List users (authenticated)
// ---------------------------------------------------------------------------

async function fetchUsers(): Promise<User[]> {
  const response = await fetch(`${API_BASE_URL}/users`, {
    credentials: 'include',
    headers: createJsonHeaders(),
  });

  if (!response.ok) {
    await handleErrorResponse(response, 'Failed to fetch users');
  }

  return response.json();
}

export function useUsersQuery(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: userKeys.lists(),
    queryFn: fetchUsers,
    enabled: options?.enabled ?? true,
    retry: (failureCount, error: unknown) => {
      if (error && typeof error === 'object' && 'retryable' in error) {
        return Boolean((error as UserApiError).retryable) && failureCount < 3;
      }
      return failureCount < 3;
    },
  });
}

// ---------------------------------------------------------------------------
// Update user (authenticated)
// ---------------------------------------------------------------------------

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  role?: UserRole;
}

async function updateUser(userId: string, payload: UpdateUserPayload): Promise<User> {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createJsonHeaders(),
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    await handleErrorResponse(response, 'Failed to update user');
  }

  return response.json();
}

export function useUpdateUserMutation(options?: {
  onSuccess?: (data: User) => void;
  onError?: (error: UserApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, payload }: { userId: string; payload: UpdateUserPayload }) =>
      updateUser(userId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      options?.onSuccess?.(data);
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as UserApiError);
      }
    },
  });
}

// ---------------------------------------------------------------------------
// Delete user (authenticated)
// ---------------------------------------------------------------------------

async function deleteUser(userId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createJsonHeaders(),
  });

  if (!response.ok) {
    await handleErrorResponse(response, 'Failed to delete user');
  }
}

export function useDeleteUserMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: UserApiError) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      if (error && typeof error === 'object' && 'status' in error) {
        options?.onError?.(error as UserApiError);
      }
    },
  });
}
